import { createFileRoute, redirect, useRouteContext } from '@tanstack/react-router';
import { message, Spin } from 'antd';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { useCreateBillingCheckoutSession } from '../../../api/generated/api';
import { getAttribution } from '../../../modules/attribution';
import { CenteredLayoutContent } from '../../../ui/components/layout';
import { storage } from '../../../ui/utils/local-storage';

import type { GetAccountResponseDto } from '../../../api/generated/model';

const OnboardingBillingRoute = () => {
  const { account } = useRouteContext({ from: '/_authenticated' });

  const selectedOrganizationId = storage.getJSON<Record<string, string>>(
    'selectedOrganizationId',
    {},
  )[account.id];

  const selectedMembership =
    account.memberships?.find(({ organization: { id } }) => id === selectedOrganizationId) ??
    account.memberships?.[0];

  const organizationId = selectedMembership?.organization.id;
  const canManageBilling = Boolean(selectedMembership?.role.permissions.settings.can.manageBilling);

  const checkoutMutation = useCreateBillingCheckoutSession({
    mutation: {
      onSuccess: (res) => {
        if (res.data.url) {
          window.location.href = res.data.url;
        }
      },
      onError: () => message.error('Failed to start checkout session'),
    },
  });

  const startedRef = useRef(false);
  useEffect(() => {
    if (!organizationId || !canManageBilling || startedRef.current) {
      return;
    }
    startedRef.current = true;
    // Attribution rides to Stripe here; the trial webhook reads it back for conversion tracking.
    checkoutMutation.mutate({ organizationId, data: { attribution: getAttribution() } });
  }, [organizationId, canManageBilling, checkoutMutation]);

  if (!organizationId) {
    return null;
  }

  if (!canManageBilling) {
    return (
      <CenteredLayoutContent
        title="Billing setup required"
        subtitle="Your workspace needs an active subscription."
        width={480}
      />
    );
  }

  return (
    <CenteredLayoutContent
      title="Redirecting to checkout"
      subtitle="Taking you to Stripe to complete your subscription."
      width={360}
    >
      <Spin size="large" />
    </CenteredLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/onboarding/billing')({
  validateSearch: z.object({
    session_id: z.string().optional(),
  }),
  /**
   * This route starts a Stripe Checkout session on mount. Without a guard, any path that
   * lands here with a subscription already in place — notably a Stripe return that raced
   * the webhook — would silently push the user through checkout a second time.
   */
  beforeLoad: (options: {
    context: { account: GetAccountResponseDto };
    search: { session_id?: string };
  }) => {
    const { account } = options.context;

    if (options.search.session_id) {
      throw redirect({
        to: '/billing/return',
        search: { session_id: options.search.session_id },
        replace: true,
      });
    }

    const selectedOrganizationId = storage.getJSON<Record<string, string>>(
      'selectedOrganizationId',
      {},
    )[account.id];

    const selectedMembership =
      account.memberships?.find(({ organization }) => organization.id === selectedOrganizationId) ??
      account.memberships?.[0];

    if (selectedMembership?.organization.hasActiveSubscription) {
      throw redirect({ to: '/records' });
    }
  },
  component: OnboardingBillingRoute,
});
